// Het tabblad Gebruik van het vlak Gebruikers & engagement (W6 ronde 7; research §3.8).
//
// Wie gebruikt het platform, hoe vaak, en met welk krediet — per persoon, over
// de laatste 7, 30 of 90 dagen (GET /api/beheer/engagement).
//
// AVG en V2: alleen tellingen en tijden, nooit inhoud. De server stuurt geen
// vraag, geen antwoord en geen opmerking mee — alleen HOEVEEL (bezoeken,
// gesprekken, duimen, opmerkingen, verzoeken) en WANNEER. Dit tabblad tekent
// bovendien alleen de velden die het kent: stuurt de server ooit per ongeluk
// meer mee, dan komt dat hier niet in beeld. Wat iemand vroeg, staat alleen
// onder Kwaliteit.
//
// De regel die hier het zwaarst weegt: de server is de baas. Hij stuurt elke
// telling, de totalen, de zinnen (privacy, periode, het totaal, de tijdschatting
// in woorden en haar uitleg, wat "verzoeken" telt, en waarom een actieve rij
// geen schatting heeft: `tijdReden`) en bij een lege rij de reden. Wat "actief" is (een dag met een
// actie, ook een schermwissel) bepaalt de server; dit tabblad rekent
// geen getal, geen aandeel, geen duur en geen oordeel uit; het stoplicht bij
// het krediet is de stand van de server.
//
// Het krediet per soort is dezelfde regel als in Kosten & credits (`SoortRegel`,
// met het woord uit woorden.ts): één as, één woordenlijst, één plek. Hier kan de
// beheerder daarnaast een persoonlijk maximum zetten of weghalen (PUT
// /api/beheer/gebruikers/{id}/krediet). Dat SCHRIJFT in de gedeelde database,
// dus dezelfde afspraken als Instellingen onder Configuratie (ronde 6):
//  · de server stuurt elke kredietsoort, ook zonder maximum en zonder verbruik
//    (stand "leeg"): zo kan er altijd een eerste persoonlijk maximum op, en
//    blijft de regel staan na "Terug naar de rolstandaard";
//  · één opslag tegelijk: tijdens het opslaan staan alle formulieren dicht, en
//    ze gaan pas weer open als de lijst opnieuw is opgehaald — zo klikt niemand
//    verder op een kaart die nog de oude stand toont. "Dicht" is aria-disabled
//    en alleen-lezen, niet `disabled`: een knop die zichzelf uitzet terwijl hij
//    de focus heeft, laat die op de pagina vallen;
//  · elke PUT stuurt `maximum` mee — een heel getal, of null voor "terug naar
//    de rolstandaard". Het contract eist de sleutel: weglaten wiste vroeger
//    stil een persoonlijk maximum (review ronde 7);
//  · een persoonlijk maximum van 0 is een blokkade: de server laat die regel
//    staan (rood, `persoonlijk`), dus ook "Terug naar de rolstandaard";
//  · het scherm leest alleen of er een heel getal van 0 of meer staat
//    (`leesGetal`, zoals een Nederlander typt: "1.000" is duizend). Het bereik
//    controleert de server; zijn 400/404/422-zin staat er letterlijk;
//  · de zin van de server ("Het persoonlijke maximum is nu 40.") wordt gemeld
//    (aria-live) zodra de nieuwe lijst binnen is.
//
// Volgnummers: zelfde afspraak als Configuratie. Elke ophaalvraag krijgt een
// nummer, zodat dit tabblad weet wanneer de ronde ná het opslaan binnen is —
// gelukt of mislukt — ook als de server precies hetzelfde terugstuurt.

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { Card, ReasonBadge } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useMetaResource, type MetaResource } from "../../lib/meta";
import { useResource } from "../../lib/useResource";
import type {
  EngagementPersoon,
  EngagementResponse,
  KredietRequest,
  KredietSoort,
} from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import { aantal, Blok, cijfer, datum, getal, Kpi, noot } from "./BronnenStukken";
import { focusKwijt, leesGetal } from "./ConfiguratieInstellingen";
import { DuimIcoon, KlokIcoon, SlotIcoon, TerugIcoon, VinkIcoon, WachtrijIcoon } from "./Iconen";
import { OpenVerzoeken, SoortRegel } from "./KostenCredits";
import { StatusPil } from "./StatusPil";
import { invoer, knopDonker, knopLicht } from "./stijl";
import { DUIM_LABEL, geenRolReden, kredietLabel, leesbaar } from "./woorden";

/** Het antwoord met het volgnummer van de vraag die het ophaalde. */
type Binnen = { data: EngagementResponse; ronde: number };

/** Slaat op; geeft de foutzin terug, of null als het lukte. */
type Opslaan = (persoon: EngagementPersoon, body: KredietRequest) => Promise<string | null>;

/** Waar het tabblad mee opent (de standaard van de server, research §3.8). */
const STANDAARD_DAGEN = 30;

/** Opgeslagen, maar de nieuwe lijst kwam niet: de kaart toont nog de oude
 *  stand, dus de zin van de server ("is nu 40") zou naast een 20 staan. De
 *  knop staat bovenaan, bij de fout. */
const NIET_BIJGEWERKT =
  "Opgeslagen, maar de lijst kon niet worden bijgewerkt — kies bovenaan Opnieuw proberen.";

/** Een actieve rij zonder schatting hoort een `tijdReden` van de server te
 *  dragen. Ontbreekt die toch, dan zegt het vak dat — nooit een lege tegel en
 *  geen eigen gok naar het waarom. */
const GEEN_TIJDREDEN = "de server gaf geen schatting en geen reden";

function naamVan(p: EngagementPersoon): string {
  return p.naam || p.email;
}

/** Eén opslag tegelijk: welke persoon, welke soort. */
function sleutel(id: string, soort: string): string {
  return `${id}:${soort}`;
}

/** "Beheer/Bronnen/Corpus" → "Beheer › Bronnen › Corpus". Het blijft het pad
 *  zoals het gebruikslog het kent — geen eigen namen, alleen een leesbaarder
 *  scheidingsteken. */
function schermPad(scherm: string): string {
  return scherm.split("/").join(" › ");
}

export function Gebruik({ lijstVersie }: { lijstVersie?: unknown }) {
  const [dagen, setDagen] = useState(STANDAARD_DAGEN);
  const gevraagd = useRef(0);
  // Een mislukte vraag draagt geen data, dus haar volgnummer staat apart (zie
  // Configuratie.tsx). Alleen de nieuwste vraag telt.
  const [misluktRonde, setMisluktRonde] = useState(0);
  const bron = useResource<Binnen>(() => {
    const ronde = ++gevraagd.current;
    return api.engagement(dagen).then(
      (data) => ({ data, ronde }),
      (err: unknown) => {
        if (ronde === gevraagd.current) setMisluktRonde(ronde);
        throw err;
      },
    );
  }, [dagen]);
  // De sectornamen staan in /api/meta (gecachet, de pulsbalk haalt hem al op);
  // de server stuurt bij een persoon alleen de sleutels van zijn sectoren.
  const meta = useMetaResource();

  // Veranderde de lijst op Mensen (een uitnodiging, een andere rol), dan klopt
  // deze lijst niet meer: opnieuw ophalen. Niet bij het openen — dan haalt
  // useResource al op.
  const vorigeVersie = useRef(lijstVersie);
  useEffect(() => {
    if (lijstVersie === vorigeVersie.current) return;
    vorigeVersie.current = lijstVersie;
    bron.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- alleen bij een nieuwe lijst
  }, [lijstVersie]);

  const [bezig, setBezig] = useState<string | null>(null);
  const [melding, setMelding] = useState("");
  // Opgeslagen, maar de nieuwe lijst is nog niet terug: `na` is de laatste
  // vraag van vóór het opslaan, `melding` wat er daarna gemeld wordt.
  const [wacht, setWacht] = useState<{ na: number; melding: string } | null>(null);
  // Opgeslagen, maar het ophalen daarna mislukte: de melding die hoort bij de
  // eerste ronde ná `na` die wél lukt (de knop Opnieuw proberen bovenaan).
  const [achter, setAchter] = useState<{ na: number; melding: string } | null>(null);

  const dataRonde = bron.data?.ronde ?? 0;
  const binnen = Math.max(dataRonde, misluktRonde);
  const ophaalMislukt = misluktRonde > dataRonde;

  // Pas als een antwoord van ná het opslaan binnen is, gaan de formulieren
  // open en klinkt de zin van de server.
  useEffect(() => {
    if (wacht && binnen > wacht.na) {
      setWacht(null);
      setBezig(null);
      if (ophaalMislukt) {
        setMelding(NIET_BIJGEWERKT);
        setAchter({ na: binnen, melding: wacht.melding });
      } else {
        setMelding(wacht.melding);
      }
      return;
    }
    if (achter && !ophaalMislukt && binnen > achter.na) {
      setAchter(null);
      setMelding(achter.melding);
    }
  }, [wacht, achter, binnen, ophaalMislukt]);

  const opslaan: Opslaan = async (persoon, body) => {
    setBezig(sleutel(persoon.id, body.soort));
    setAchter(null);
    setMelding("Het krediet wordt opgeslagen…");
    let tekst: string;
    try {
      tekst = (await api.kredietZetten(persoon.id, body)).tekst;
    } catch (err) {
      setBezig(null);
      setMelding("");
      return leesbaar(err, "Het krediet kon niet worden opgeslagen — probeer het zo nog eens.");
    }
    // De zin is die van de server; naam en soort ervoor, zodat een schermlezer
    // hoort wiens krediet het was.
    const na = gevraagd.current;
    bron.reload();
    setWacht({ na, melding: `${naamVan(persoon)} · ${kredietLabel(body.soort)}: ${tekst}` });
    return null;
  };

  const d = bron.data?.data ?? null;
  if (!d) {
    return (
      <Card style={{ padding: 18 }}>
        {bron.error ? (
          <LoadError reason={bron.error} onRetry={bron.reload} />
        ) : (
          <EmptyReason reason="het gebruik wordt opgehaald" />
        )}
      </Card>
    );
  }

  const uit = bezig !== null;
  // Mislukte een andere periode, dan staan hieronder nog de cijfers van de
  // vorige, onder een knop die de nieuwe als gekozen toont. Daarom dimmen zoals
  // bij een lopende vraag, en zeggen van welke periode ze zijn (de fout en
  // Opnieuw proberen staan erboven). Tijdens het ophalen zelf alleen dimmen.
  const anderePeriode = d.periode.dagen !== dagen;
  const dim = bron.stale || anderePeriode ? 0.6 : 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <p
        role="note"
        data-privacy
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          margin: 0,
          padding: "10px 14px",
          background: color.surfaceQuote,
          border: `1px solid ${color.line}`,
          borderRadius: radius.block,
          fontSize: size.meta,
          color: color.body,
          lineHeight: 1.55,
        }}
      >
        <span style={{ color: color.soft, marginTop: 2 }}>
          <SlotIcoon />
        </span>
        <span>{d.privacy}</span>
      </p>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        {/* De periodes komen van de server; hetzelfde gesegmenteerde patroon
            als de tabbladen. Gekozen = wat gevraagd is, ook als het antwoord
            nog onderweg is (de kaarten dimmen dan). */}
        <nav
          aria-label="Periode"
          style={{
            display: "inline-flex",
            flexWrap: "wrap",
            gap: 4,
            background: color.lineFaint,
            borderRadius: radius.input,
            padding: 3,
          }}
        >
          {d.periodes.map((p) => (
            <button
              key={p}
              type="button"
              className={"view-btn" + (dagen === p ? " active" : "")}
              aria-pressed={dagen === p}
              onClick={() => setDagen(p)}
            >
              {`${getal(p)} dagen`}
            </button>
          ))}
        </nav>
        <span style={{ fontSize: size.meta, color: color.soft, opacity: dim }}>{d.periode.uitleg}</span>
      </div>

      {/* Mislukte een herlaadronde, dan blijft de vorige stand staan, met de
          fout en de knop om het opnieuw te proberen erboven. */}
      {bron.error && (
        <Card style={{ padding: 18 }}>
          <LoadError reason={bron.error} onRetry={bron.reload} />
        </Card>
      )}

      {/* Een levend gebied dat er altijd staat: een schermlezer meldt zowel
          "wordt opgeslagen" als de zin van de server. */}
      <div role="status" aria-live="polite" data-krediet-status>
        {melding && <p style={{ ...noot, marginTop: 0, color: color.body }}>{melding}</p>}
      </div>

      {anderePeriode && !bron.loading && (
        <p data-oude-periode style={{ ...noot, marginTop: 0, color: color.body }}>
          {`Hieronder staan nog de cijfers van de laatste ${getal(d.periode.dagen)} dagen.`}
        </p>
      )}

      <div
        data-inhoud
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 18,
          opacity: dim,
          transition: "opacity 0.15s",
        }}
      >
        <Totaal d={d} />

        <Blok titel="Per persoon">
          {d.personen.length === 0 ? (
            <Card style={{ padding: 18 }}>
              <EmptyReason reason="er staat nog niemand in de gebruikerslijst" />
            </Card>
          ) : (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(min(420px, 100%), 1fr))",
                gap: 12,
              }}
            >
              {d.personen.map((p) => (
                <PersoonKaart
                  key={p.id}
                  p={p}
                  verzoekenUitleg={d.verzoekenUitleg}
                  meta={meta}
                  uit={uit}
                  bezig={bezig}
                  onOpslaan={opslaan}
                />
              ))}
            </ul>
          )}
        </Blok>
      </div>

      <p style={{ ...noot, marginTop: 0 }}>
        {`Gemeten op ${datum(d.gemetenOp) ?? "een onbekend moment"}.`}
      </p>
    </div>
  );
}

/** De zin van de server als grote regel, de drie totalen als kaart (owner-
 *  ontwerptaal: grote getallen in een kaart) en één keer de uitleg van wat
 *  "verzoeken" telt en van de tijdschatting — die gelden voor elke persoon
 *  hieronder. Beide zinnen zijn van de server. */
function Totaal({ d }: { d: EngagementResponse }) {
  return (
    <section aria-label="Totaal in deze periode" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <Card style={{ padding: "16px 18px" }}>
        <p
          data-totaal
          style={{
            margin: 0,
            fontSize: 20,
            fontWeight: weight.bold,
            color: color.ink,
            letterSpacing: "-0.01em",
            lineHeight: 1.3,
          }}
        >
          {d.totaal.tekst}
        </p>
      </Card>
      <div
        data-totalen
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(min(180px, 100%), 1fr))",
          gap: 12,
        }}
      >
        <Kpi label="Bezoeken" waarde={getal(d.totaal.bezoeken)} />
        <Kpi label="Gesprekken" waarde={getal(d.totaal.gesprekken)} />
        <Kpi label="Verzoeken" waarde={getal(d.totaal.verzoeken)} />
      </div>
      {/* Het woord alleen was dubbelzinnig naast de kredietverzoeken (review
          frontend ronde 7). Kpi kent geen title; de zin staat er dus onder. */}
      <p
        data-verzoeken-uitleg
        style={{ ...noot, marginTop: 0, display: "flex", alignItems: "flex-start", gap: 8 }}
      >
        <span style={{ marginTop: 2 }}>
          <WachtrijIcoon />
        </span>
        <span>{d.verzoekenUitleg}</span>
      </p>
      <p
        data-tijd-uitleg
        style={{ ...noot, marginTop: 0, display: "flex", alignItems: "flex-start", gap: 8 }}
      >
        <span style={{ marginTop: 2 }}>
          <KlokIcoon />
        </span>
        <span>{d.tijdUitleg}</span>
      </p>
    </section>
  );
}

function PersoonKaart({
  p,
  verzoekenUitleg,
  meta,
  uit,
  bezig,
  onOpslaan,
}: {
  p: EngagementPersoon;
  /** De zin van de server bij "Verzoeken", als title op de tegel. */
  verzoekenUitleg: string;
  meta: MetaResource;
  /** Er loopt ergens een opslag: alle formulieren dicht. */
  uit: boolean;
  /** Welke opslag loopt (persoon:soort), of null. */
  bezig: string | null;
  onOpslaan: Opslaan;
}) {
  return (
    <li data-persoon={p.id}>
      <Card
        style={{
          padding: "16px 18px",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        <Wie p={p} meta={meta} />
        <Activiteit p={p} verzoekenUitleg={verzoekenUitleg} />
        <Krediet p={p} uit={uit} bezig={bezig} onOpslaan={onOpslaan} />
      </Card>
    </li>
  );
}

function Wie({ p, meta }: { p: EngagementPersoon; meta: MetaResource }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <h3 style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink, margin: 0 }}>
          {naamVan(p)}
        </h3>
        <StatusPil status={p.status} />
        {/* Zoals in Kosten & credits: de badge alleen náást een rol. Zonder rol
            zegt "beheert alleen" hieronder het al, en stond het er dubbel. */}
        {p.isAdmin && p.rolLabel && (
          <ReasonBadge text="beheerder" title="Mag uitnodigen en de wachtrij beslissen." />
        )}
      </div>
      {p.naam && (
        <div style={{ fontSize: size.meta, color: color.faint, overflowWrap: "anywhere", marginTop: -4 }}>
          {p.email}
        </div>
      )}
      <dl style={{ margin: 0, display: "flex", flexDirection: "column", gap: 4 }}>
        <Feit label="Rol">
          {/* Geen rol is geldig zolang je beheerder bent (W9-besluit B3):
              dezelfde woorden als op Mensen en in Kosten & credits. */}
          {p.rolLabel ?? <EmptyReason reason={geenRolReden(p.isAdmin)} />}
        </Feit>
        <Feit label="Propositie">
          <Propositie p={p} meta={meta} />
        </Feit>
        <Feit label="Uitgenodigd">
          <Uitnodiging p={p} />
        </Feit>
        <Feit label="Laatst actief">
          {datum(p.laatstActief) ?? <EmptyReason reason="nog nooit actief" />}
        </Feit>
      </dl>
    </div>
  );
}

function Feit({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "104px 1fr", gap: 8, alignItems: "baseline" }}>
      <dt style={{ fontSize: 12, color: color.faint }}>{label}</dt>
      <dd style={{ margin: 0, fontSize: size.meta, color: color.body, minWidth: 0 }}>{children}</dd>
    </div>
  );
}

/** De propositie met haar sectoren, in de woorden van /api/meta. Een sleutel
 *  die meta niet kent, staat er kaal (zelfde afspraak als woorden.ts). */
function Propositie({ p, meta }: { p: EngagementPersoon; meta: MetaResource }) {
  if (!p.propositieLabel) return <EmptyReason reason="alle proposities" />;
  const klein = { fontSize: 12 } as const;
  let sectoren: ReactNode;
  if (p.sectoren.length === 0) {
    sectoren = <EmptyReason reason="raakt geen marktsector" style={klein} />;
  } else if (meta.data) {
    const lijst = meta.data.sectors ?? [];
    sectoren = p.sectoren.map((k) => lijst.find((s) => s.key === k)?.label ?? k).join(" · ");
  } else {
    sectoren = (
      <EmptyReason
        reason={
          meta.error
            ? "de namen van de sectoren konden niet worden opgehaald"
            : "de namen van de sectoren worden opgehaald"
        }
        style={klein}
      />
    );
  }
  return (
    <>
      <div>{p.propositieLabel}</div>
      <div data-sectoren style={{ ...klein, color: color.soft, marginTop: 2 }}>
        {sectoren}
      </div>
    </>
  );
}

/** "door Anne Meijer op 24 aug. 2026" — wat er is eerst, in de volgorde van
 *  de zin, en daarachter de reden voor wat ontbreekt ("op 24 aug. 2026 · door
 *  wie is niet vastgelegd"). */
function Uitnodiging({ p }: { p: EngagementPersoon }) {
  const door = p.uitgenodigdDoor;
  const op = datum(p.uitgenodigdOp);
  if (door && op) return <>{`door ${door} op ${op}`}</>;
  if (op) {
    return (
      <>
        {`op ${op} · `}
        <EmptyReason reason="door wie is niet vastgelegd" />
      </>
    );
  }
  if (door) {
    return (
      <>
        {`door ${door} · `}
        <EmptyReason reason="datum niet vastgelegd" />
      </>
    );
  }
  return <EmptyReason reason="niet vastgelegd" />;
}

const kopje = { ...sectionLabel, margin: "0 0 8px" } as const;

function Activiteit({ p, verzoekenUitleg }: { p: EngagementPersoon; verzoekenUitleg: string }) {
  return (
    <section aria-label={`Gebruik van ${naamVan(p)} in deze periode`}>
      <h4 style={kopje}>In deze periode</h4>
      {p.reden ? (
        // Niets gedaan: de zin van de server in plaats van een rij nullen.
        <EmptyReason reason={p.reden} />
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(min(118px, 100%), 1fr))",
              gap: 8,
            }}
          >
            <Tegel label="Bezoeken" waarde={getal(p.bezoeken)} />
            <Tegel label="Dagen actief" waarde={getal(p.dagenActief)} />
            <Tegel label="Gesprekken" waarde={getal(p.gesprekken)}>
              <Duimen p={p} />
            </Tegel>
            <Tegel label="Verzoeken" waarde={getal(p.verzoeken)} title={verzoekenUitleg} />
            <Tegel
              label="Tijd"
              waarde={
                p.tijdTekst ? (
                  <>
                    <span>{p.tijdTekst}</span>{" "}
                    <ReasonBadge
                      text="schatting"
                      tone="amber"
                      title="Een schatting — de uitleg staat boven de lijst."
                    />
                  </>
                ) : null
              }
              // De zin van de server ("Eén losse actie, of …"). Een stille rij
              // komt hier niet: die toont hierboven alleen haar `reden`.
              reden={p.tijdReden ?? GEEN_TIJDREDEN}
            />
          </div>
          <Schermen schermen={p.schermen} />
        </>
      )}
    </section>
  );
}

function Tegel({
  label,
  waarde,
  reden,
  title,
  children,
}: {
  label: string;
  waarde: ReactNode | null;
  reden?: string;
  /** Een zin van de server die zegt wat het getal telt. */
  title?: string;
  children?: ReactNode;
}) {
  return (
    <div
      data-tegel
      title={title}
      style={{
        background: color.surfaceQuote,
        border: `1px solid ${color.lineFaint}`,
        borderRadius: radius.block,
        padding: "9px 11px",
        minWidth: 0,
      }}
    >
      <div style={{ ...sectionLabel, fontSize: 10, marginBottom: 4 }}>{label}</div>
      {waarde !== null ? (
        <div style={{ fontSize: 17, fontWeight: weight.bold, color: color.ink, lineHeight: 1.25, ...cijfer }}>
          {waarde}
        </div>
      ) : (
        <EmptyReason reason={reden ?? "de server gaf geen getal"} style={{ fontSize: 12 }} />
      )}
      {children}
    </div>
  );
}

/** De duimen en opmerkingen onder de gesprekken: geteld, nooit gelezen. */
function Duimen({ p }: { p: EngagementPersoon }) {
  const regel = { display: "inline-flex", alignItems: "center", gap: 4 } as const;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        marginTop: 4,
        fontSize: 11,
        color: color.soft,
        ...cijfer,
      }}
    >
      <span style={regel}>
        <DuimIcoon /> {`${getal(p.duimOp)} ${DUIM_LABEL.op}`}
      </span>
      <span style={regel}>
        <DuimIcoon omlaag /> {`${getal(p.duimNeer)} ${DUIM_LABEL.neer}`}
      </span>
      <span title="Alleen geteld — wat erin staat, is alleen onder Kwaliteit te zien.">
        {aantal(p.opmerkingen, "opmerking", "opmerkingen")}
      </span>
    </div>
  );
}

function Schermen({ schermen }: { schermen: EngagementPersoon["schermen"] }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ fontSize: 12, color: color.faint, marginBottom: 4 }}>Meest geopende schermen</div>
      {schermen.length === 0 ? (
        <EmptyReason reason="geen scherm geopend in deze periode" />
      ) : (
        <ol style={{ margin: 0, paddingLeft: 18, fontSize: size.meta, color: color.body, lineHeight: 1.6 }}>
          {schermen.map((s, i) => (
            <li key={`${i}-${s.scherm ?? ""}`} data-scherm>
              {s.scherm ? schermPad(s.scherm) : <EmptyReason reason="scherm niet vastgelegd" />}
              <span style={{ color: color.faint, ...cijfer }}>{` · ${aantal(s.keer, "keer", "keer")}`}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Krediet({
  p,
  uit,
  bezig,
  onOpslaan,
}: {
  p: EngagementPersoon;
  uit: boolean;
  bezig: string | null;
  onOpslaan: Opslaan;
}) {
  return (
    <section
      aria-label={`Krediet van ${naamVan(p)}`}
      style={{ borderTop: `1px solid ${color.lineFaint}`, paddingTop: 12, marginTop: "auto" }}
    >
      <h4 style={kopje}>Krediet</h4>
      {p.krediet.length === 0 ? (
        <EmptyReason reason="geen krediet en niets gebruikt" />
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 14 }}>
          {p.krediet.map((s) => (
            <KredietRegel
              key={s.soort}
              persoon={p}
              s={s}
              uit={uit}
              bezig={bezig === sleutel(p.id, s.soort)}
              onOpslaan={onOpslaan}
            />
          ))}
        </ul>
      )}
      <div style={{ marginTop: 12, fontSize: size.meta, color: color.body }}>
        <span style={{ color: color.faint }}>Kredietverzoeken: </span>
        <OpenVerzoeken n={p.openKredietverzoeken} />
      </div>
    </section>
  );
}

function KredietRegel({
  persoon,
  s,
  uit,
  bezig,
  onOpslaan,
}: {
  persoon: EngagementPersoon;
  s: KredietSoort;
  /** Er loopt ergens een opslag: alle formulieren dicht. */
  uit: boolean;
  /** De opslag van déze regel loopt. */
  bezig: boolean;
  onOpslaan: Opslaan;
}) {
  const id = useId();
  const naam = `${naamVan(persoon)} · ${kredietLabel(s.soort)}`;
  const [tekst, setTekst] = useState("");
  const [fout, setFout] = useState<string | null>(null);
  // Deze regel sloeg op en het lukte; het veld wacht op het einde van `bezig`.
  const [gelukt, setGelukt] = useState(false);
  const opslaanKnop = useRef<HTMLButtonElement>(null);
  const terugFocus = useRef(false);

  // Veranderde de stand (na een opslag, van hier of van iemand anders), dan is
  // het getypte getal verwerkt en hoort een foutzin bij de oude stand: veld
  // leeg, fout weg. Bij een ronde waarin deze soort gelijk bleef (een andere
  // periode) blijft getypte tekst staan — ook in regels die niets opsloegen.
  useEffect(() => {
    setTekst("");
    setFout(null);
  }, [s.maximum, s.persoonlijk]);

  // Dezelfde waarde opgeslagen: dan verandert de stand niet en vuurt het
  // effect hierboven niet. Na een gelukte eigen opslag begint het veld daarom
  // ook leeg, zodra deze regel klaar is — de nieuwe lijst is binnen, of het
  // ophalen mislukte (opgeslagen is opgeslagen; de melding zegt de rest).
  useEffect(() => {
    if (bezig || !gelukt) return;
    setGelukt(false);
    setTekst("");
    setFout(null);
  }, [bezig, gelukt]);

  // De knoppen zetten zichzelf niet uit (aria-disabled), dus wie op Opslaan
  // klikte houdt de focus. Eén knop verdwijnt wel: "Terug naar de
  // rolstandaard" is er na het terugzetten niet meer, en dan valt de focus op
  // de pagina. Zodra deze regel klaar is, krijgt Opslaan hem — alleen als hij
  // kwijt is: wie intussen elders stond, houdt zijn plek. Een layout-effect:
  // de focus staat er in dezelfde weergave waarin de regel klaar is (zie
  // ConfiguratieInstellingen; een gewoon effect liep daar in de CI achteraan).
  useLayoutEffect(() => {
    if (bezig || !terugFocus.current) return;
    terugFocus.current = false;
    if (focusKwijt()) opslaanKnop.current?.focus();
  }, [bezig]);

  // Geen eigen bereikcontrole (die doet de server): alleen "staat er een heel
  // getal van 0 of meer". "2,5", "-3" of "abc" gaan nooit de deur uit.
  const n = leesGetal(tekst);
  const heel = n !== null && Number.isInteger(n) && n >= 0 ? n : null;
  const leeg = tekst.trim() === "";
  const onleesbaar = !leeg && heel === null;
  const kan = !uit && heel !== null;

  // Beide wegen sturen `soort` én `maximum` (zie de kop): het type eist het.
  async function stuur(body: KredietRequest) {
    setFout(null);
    terugFocus.current = true;
    const zin = await onOpslaan(persoon, body);
    setFout(zin);
    if (zin === null) setGelukt(true);
  }

  function verstuur(e: FormEvent) {
    e.preventDefault();
    if (!kan || heel === null) return;
    void stuur({ soort: s.soort, maximum: heel });
  }

  function terug() {
    if (uit) return;
    // null = het persoonlijke maximum weg; de standaard van de rol geldt weer.
    void stuur({ soort: s.soort, maximum: null });
  }

  return (
    <li data-krediet={s.soort} aria-busy={bezig || undefined}>
      <SoortRegel soort={s} />
      <form
        noValidate
        onSubmit={verstuur}
        aria-label={`${naam} wijzigen`}
        style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: 8 }}
      >
        <label htmlFor={`${id}-max`} style={{ fontSize: 12, color: color.faint }}>
          Persoonlijk maximum
        </label>
        {/* Tekst, geen getalveld: dat las "1.000" als 1 (zie
            ConfiguratieInstellingen). `inputMode` geeft op een telefoon toch
            het cijferklavier. Tijdens een opslag alleen-lezen, niet uit. */}
        <input
          id={`${id}-max`}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          value={tekst}
          readOnly={uit}
          onChange={(e) => {
            setTekst(e.target.value);
            setFout(null);
          }}
          aria-label={`Persoonlijk maximum voor ${naam}`}
          aria-invalid={onleesbaar}
          aria-describedby={`${id}-hint`}
          style={{ ...invoer, width: 96, padding: "7px 10px", fontSize: size.meta }}
        />
        <span aria-hidden style={{ fontSize: size.meta, color: color.soft, whiteSpace: "nowrap" }}>
          {kredietLabel(s.soort).toLowerCase()}
        </span>
        {/* aria-disabled, niet disabled (zie de kop): verstuur() en terug()
            houden zelf tegen wat nu niet kan. */}
        <button
          ref={opslaanKnop}
          type="submit"
          aria-disabled={!kan}
          style={{
            ...knopDonker(!kan),
            padding: "7px 12px",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <VinkIcoon /> {bezig ? "bezig…" : "Opslaan"}
        </button>
        {s.persoonlijk && (
          <button
            type="button"
            onClick={terug}
            aria-disabled={uit}
            style={{
              ...knopLicht,
              padding: "7px 12px",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: uit ? color.faint : color.body,
              cursor: uit ? "default" : "pointer",
            }}
          >
            <TerugIcoon /> Terug naar de rolstandaard
          </button>
        )}
      </form>
      {/* Niet alleen rood: de zin zegt wat er moet gebeuren. */}
      <p
        id={`${id}-hint`}
        style={{ fontSize: 12, margin: "6px 0 0", color: onleesbaar ? color.accentRed : color.faint }}
      >
        {onleesbaar
          ? "Vul een heel getal in, 0 of meer."
          : s.persoonlijk
            ? "Een nieuw getal vervangt het persoonlijke maximum."
            : "Een heel getal, 0 of meer. Het gaat vóór de standaard van de rol."}
      </p>
      {fout && (
        <p role="alert" style={{ fontSize: size.meta, color: color.accentRed, margin: "6px 0 0" }}>
          {fout}
        </p>
      )}
    </li>
  );
}
